import React, { useEffect, useRef, useState } from "react";

import { makeKeyActivationHandler } from "utils";

import { PageLayout } from "./PageLayout";

interface Props {
  minLeftWidth: number;
  maxLeftWidth: number;
  minRightWidth: number;
  maxRightWidth: number;
  defaultLeftWidth: number;
  leftChildren: React.ReactNode;
  rightChildren: React.ReactNode;
  leftWidthOverride?: number;
  onLeftColumnClick?: VoidFunction;
}

export function TwoElementResizablePage(props: Props) {
  const {
    minLeftWidth,
    maxLeftWidth,
    minRightWidth,
    maxRightWidth,
    defaultLeftWidth,
    leftChildren,
    rightChildren,
    leftWidthOverride,
    onLeftColumnClick,
  } = props;

  const [isHovered, setIsHovered] = useState(false);
  const resizerRef = useRef<HTMLDivElement | null>(null);
  const leftSideRef = useRef<HTMLDivElement | null>(null);
  const rightSideRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = leftSideRef.current;
    if (el) {
      el.style.transition = "width 0.3s ease";
      el.style.width =
        leftWidthOverride !== undefined
          ? `${leftWidthOverride}%`
          : `${defaultLeftWidth}%`;
      const timer = setTimeout(() => {
        el.style.transition = "";
      }, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [leftWidthOverride, defaultLeftWidth]);

  useEffect(() => {
    const resizer = resizerRef.current;
    const leftSide = leftSideRef.current;
    const rightSide = rightSideRef.current;

    if (!resizer || !leftSide || !rightSide) return;

    let x = 0;
    let leftWidth = 0;

    const mouseMoveHandler = (e: MouseEvent) => {
      const dx = e.clientX - x;
      const newLeftWidth =
        ((leftWidth + dx) * 100) /
        (resizer.parentNode as Element).getBoundingClientRect().width;

      leftSide.style.width = `${newLeftWidth}%`;

      resizer.style.cursor = "col-resize";
      document.body.style.cursor = "col-resize";

      leftSide.style.userSelect = "none";
      leftSide.style.pointerEvents = "none";

      rightSide.style.userSelect = "none";
      rightSide.style.pointerEvents = "none";
    };

    const mouseUpHandler = () => {
      resizer.style.removeProperty("cursor");
      document.body.style.removeProperty("cursor");

      leftSide.style.removeProperty("user-select");
      leftSide.style.removeProperty("pointer-events");

      rightSide.style.removeProperty("user-select");
      rightSide.style.removeProperty("pointer-events");

      document.removeEventListener("mousemove", mouseMoveHandler);
      document.removeEventListener("mouseup", mouseUpHandler);

      if (isHovered) {
        setIsHovered(false);
      }
    };

    const mouseDownHandler = (e: MouseEvent) => {
      x = e.clientX;
      leftWidth = leftSide.getBoundingClientRect().width;

      document.addEventListener("mousemove", mouseMoveHandler);

      document.addEventListener("mouseup", mouseUpHandler);
    };

    resizer.addEventListener("mousedown", mouseDownHandler);
  }, [isHovered]);

  return (
    <PageLayout>
      <div
        style={{
          display: "flex",
          // Override default `stretch` so panes size to their own content;
          // stretching breaks children with backgrounds anchored to their natural height.
          alignItems: "flex-start",
        }}
      >
        {/* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        <div
          ref={leftSideRef}
          style={{
            width: `${defaultLeftWidth}%`,
            display: "flex",
            minWidth: `${minLeftWidth}%`,
            maxWidth: `${maxLeftWidth}%`,
            marginRight: ".1rem",
            cursor: onLeftColumnClick ? "pointer" : undefined,
          }}
          onClick={onLeftColumnClick}
          onKeyDown={
            onLeftColumnClick
              ? makeKeyActivationHandler(onLeftColumnClick)
              : undefined
          }
          tabIndex={onLeftColumnClick ? 0 : undefined}
        >
          {leftChildren}
        </div>
        {/* eslint-enable jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        <div
          ref={resizerRef}
          style={{
            cursor: isHovered ? "col-resize" : "default",
            width: "1rem",
          }}
          onMouseEnter={() => setIsHovered(true)}
          id="dragMe"
        />
        <div
          ref={rightSideRef}
          style={{
            flex: 1,
            display: "flex",
            minWidth: `${minRightWidth}%`,
            maxWidth: `${maxRightWidth}%`,
          }}
          id="rightSide"
        >
          {rightChildren}
        </div>
      </div>
    </PageLayout>
  );
}
